import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredTier?: "alpha_observer" | "alpha_participant" | "alpha_operator";
  requireSubscription?: boolean;
}

export function ProtectedRoute({ 
  children, 
  requiredTier,
  requireSubscription = true 
}: ProtectedRouteProps) {
  const { user, isLoading, effectiveTier, isAdmin, roles } = useAuth();
  const location = useLocation();

  // Wait until auth is fully loaded (including roles)
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Double-check: isAdmin is derived from roles, but we also check roles.length > 0
  // to ensure roles have actually been loaded before making subscription decisions
  const rolesLoaded = roles.length > 0 || !requireSubscription;
  const userIsAdmin = isAdmin; // roles.includes('admin')

  // Admins bypass subscription requirements
  if (!userIsAdmin && requireSubscription && !effectiveTier) {
    // User is authenticated but has no active subscription (including temp grants)
    return <Navigate to="/pricing" state={{ requireSubscription: true }} replace />;
  }

  // Tier hierarchy check
  if (requiredTier && !userIsAdmin) {
    const tierOrder = ["alpha_observer", "alpha_participant", "alpha_operator"];
    const userTierIndex = effectiveTier ? tierOrder.indexOf(effectiveTier) : -1;
    const requiredTierIndex = tierOrder.indexOf(requiredTier);

    if (userTierIndex < requiredTierIndex) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
}
